/**
 * Pokéball SVG glyph — single source of truth used in 3 places:
 *
 *   1. Orb sprite-loading silhouette (size matches sprite, opacity 0.4)
 *   2. Sprite-404 fallback when an orb GIF is missing (full opacity)
 *   3. Popover-trigger glyph at top-right of the canvas (22 px inside a
 *      38 px glass button — replaces the prior ☰ chrome)
 *
 * Inline SVG (no HTTP fetch) so the glyph works even when offline /
 * during the slow-connection first-load skeleton state. Three call sites
 * import this; never fork the SVG.
 */
const POKEBALL_SVG = `<svg viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
  <circle cx="12" cy="12" r="11" fill="currentColor" opacity="0.6"/>
  <path d="M1 12 H23" stroke="currentColor" stroke-width="2" opacity="0.8"/>
  <circle cx="12" cy="12" r="3" fill="white" stroke="currentColor" stroke-width="2"/>
</svg>`;

export interface PokeballGlyphOptions {
  /** Outer size in px (the SVG scales to fill). */
  size: number;
  /** CSS color string for currentColor. Defaults to var(--text-primary). */
  color?: string;
  /** Outer wrapper opacity (0–1). Defaults to 1. */
  opacity?: number;
}

/**
 * Build a fresh DOM element rendering the Pokéball glyph. Returns a new
 * element on every call — callers own its lifecycle.
 */
export function pokeballGlyphElement(opts: PokeballGlyphOptions): HTMLElement {
  const wrap = document.createElement('span');
  wrap.style.display = 'inline-block';
  wrap.style.width = `${opts.size}px`;
  wrap.style.height = `${opts.size}px`;
  wrap.style.color = opts.color ?? 'var(--text-primary)';
  wrap.style.opacity = String(opts.opacity ?? 1);
  wrap.style.lineHeight = '0'; // kill inline-block descender gap
  wrap.innerHTML = POKEBALL_SVG;
  return wrap;
}
